/**
 * Due dates for education series (Admin ▸ Enablement ▸ Due Dates).
 *
 * The profile Enablement table shows a due date for each item. An education series has one only when
 * Mindtickle supplies it, so an admin can enter it here. A journey map's due date is set inside the map,
 * in the builder. A date entered here never overrides one the source already has.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useScopeDraft } from '../../../hooks/education-scope-hooks';
import { useEducationCompletionByEmail } from '../../../hooks/education-completion-hooks';
import { useAuthenticateUser } from '../../../hooks/useAuthenticateUser';
import {
  EnablementItemMeta,
  EnablementMetaSource,
  enablementMetaKey,
  indexEnablementMeta,
  useEnablementItemMeta,
  useSaveEnablementItemMeta,
} from '../../../hooks/enablement-item-meta-hooks';

interface ItemRow {
  key: string;
  source: EnablementMetaSource;
  sourceId: string;
  name: string;
  detail: string;
}

const SOURCE_LABEL: Record<EnablementMetaSource, string> = {
  journey_map: 'Journey map',
  education_series: 'Education series',
};

const DueRow: React.FC<{ item: ItemRow; meta: EnablementItemMeta | undefined }> = ({ item, meta }) => {
  const saved = meta?.due_date ? meta.due_date.slice(0, 10) : '';
  const [value, setValue] = useState(saved);
  const [error, setError] = useState('');
  const save = useSaveEnablementItemMeta();
  const dirty = value !== saved;

  const submit = (next: string) => {
    setError('');
    save.mutate(
      { existing: meta, source: item.source, source_id: item.sourceId, name: item.name, due_date: next || null },
      { onError: () => setError('Could not save. Check that you have permission and try again.') },
    );
  };

  return (
    <TableRow hover>
      <TableCell>
        <Chip size="small" variant="outlined" label={SOURCE_LABEL[item.source]} />
      </TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {item.name}
        </Typography>
        {item.detail && (
          <Typography variant="caption" color="text.secondary">
            {item.detail}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <TextField
          size="small"
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputProps={{ 'aria-label': `Due date for ${item.name}` }}
          error={!!error}
          helperText={error || undefined}
        />
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            variant="contained"
            disabled={!dirty || save.isPending}
            onClick={() => submit(value)}
            startIcon={save.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Save
          </Button>
          <Button
            size="small"
            disabled={!saved || save.isPending}
            onClick={() => {
              setValue('');
              submit('');
            }}
          >
            Clear
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const EnablementDueDatesAdminPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Every series assigned anywhere in your org (Snowflake), curated for scope or not.
  const series = useScopeDraft(undefined, false, true, 'name');
  // Your own series come from the same call your profile makes, so what you see on your Enablement table is
  // always here even when the org-wide list is slow or unavailable.
  const { email } = useAuthenticateUser();
  const own = useEducationCompletionByEmail(email || undefined);
  const metaQuery = useEnablementItemMeta();
  const metaByKey = useMemo(() => indexEnablementMeta(metaQuery.data), [metaQuery.data]);

  const items = useMemo<ItemRow[]>(() => {
    const out: ItemRow[] = [];
    const seen = new Set<string>();
    const add = (id: string, name: string, detail: string) => {
      const sid = String(id || '').trim();
      if (!sid || seen.has(sid)) return;
      seen.add(sid);
      out.push({
        key: enablementMetaKey('education_series', sid),
        source: 'education_series',
        sourceId: sid,
        name: name || sid,
        detail,
      });
    };
    for (const c of series.data?.candidates ?? []) {
      const entry = c.entry ?? c.suggested;
      add(c.series_id, c.series_name, [entry?.program_label, entry ? (entry.required ? 'Required' : 'Optional') : null].filter(Boolean).join(' · '));
    }
    for (const i of own.data?.node?.own?.items ?? []) {
      add(i.series_id, i.series_name, [i.program_label, i.required ? 'Required' : 'Optional'].filter(Boolean).join(' · '));
    }
    // A series that already has a saved date stays editable even if Snowflake no longer lists it.
    for (const m of metaQuery.data ?? []) {
      if (m.source === 'education_series') add(m.source_id, m.name, '');
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [series.data, own.data, metaQuery.data]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(
      (i) => (!q || `${i.name} ${i.detail}`.toLowerCase().includes(q)),
    );
  }, [items, query]);

  const visible = shown.slice(page * pageSize, page * pageSize + pageSize);
  const loading = (series.isLoading && own.isLoading) || metaQuery.isLoading;
  const withDate = items.filter((i) => metaByKey.get(i.key)?.due_date).length;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760, mb: 2 }}>
        Enter a due date for an education series when Mindtickle has none. It shows in the Due column of each
        person&apos;s Enablement table. Journey map due dates are set inside each map, in the builder.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 260 }}
        />
        <Typography variant="caption" color="text.secondary">
          {withDate} of {items.length} have a due date
        </Typography>
      </Stack>

      {(series.isError || metaQuery.isError) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some items could not be loaded
          {series.isError ? '. The org-wide list from Snowflake is unavailable, so only your own series and ones with a saved date are shown' : ''}.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Due date</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {!loading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nothing matches.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              visible.map((i) => (
                <DueRow key={`${i.key}:${metaByKey.get(i.key)?.due_date ?? ''}`} item={i} meta={metaByKey.get(i.key)} />
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={shown.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default EnablementDueDatesAdminPage;
